import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useAdmin } from "../contexts/AdminContext";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import QRScanner from "../components/QRScanner";
import { mockApiService, shouldUseMockData } from "../mockData/mockLocketData";
import {
  colors,
  spacing,
  borderRadius,
  commonStyles,
  createButtonStyle,
  createInputStyle,
  createBadgeStyle,
} from "../styles/globalStyles";

const AdminDashboardPage = () => {
  const { isAdmin, logout } = useAdmin();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm();
  const [successMsg, setSuccessMsg] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [lockets, setLockets] = useState([]);
  const [filteredLockets, setFilteredLockets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    search: "",
    dateFrom: "",
    dateTo: "",
  });
  const [bulkGenerate, setBulkGenerate] = useState({
    count: "",
    length: "",
  });
  const [bulkGenerateLoading, setBulkGenerateLoading] = useState(false);
  const [bulkGenerateSuccess, setBulkGenerateSuccess] = useState(null);
  const [bulkGenerateError, setBulkGenerateError] = useState(null);
  const [generatedIds, setGeneratedIds] = useState([]);
  const [editingLocket, setEditingLocket] = useState(null);
  const [editFormData, setEditFormData] = useState({
    status: "",
    customerName: "",
    customerPhone: "",
    customerEmail: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editSuccess, setEditSuccess] = useState(null);
  const [editError, setEditError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [activeTab, setActiveTab] = useState("lockets"); // "lockets" or "orders"
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [ordersPagination, setOrdersPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [ordersCurrentPage, setOrdersCurrentPage] = useState(1);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderEditFormData, setOrderEditFormData] = useState({ status: "", qrCodeId: "" });

  // Redirect to login if not admin
  useEffect(() => {
    if (!isAdmin) navigate("/admin");
  }, [isAdmin, navigate]);

  // Fetch all lockets with pagination
  useEffect(() => {
    if (!isAdmin) return;
    const fetchLockets = async () => {
      setLoading(true);
      setError(null);
      try {
        let res;
        if (shouldUseMockData()) {
          res = await mockApiService.getAdminLockets();
          // For mock data, implement client-side pagination
          const startIndex = (currentPage - 1) * 25;
          const endIndex = startIndex + 25;
          const paginatedLockets = (res.data.lockets || []).slice(startIndex, endIndex);
          setLockets(res.data.lockets || []);
          setFilteredLockets(paginatedLockets);
          setPagination({
            page: currentPage,
            limit: 25,
            total: res.data.lockets?.length || 0,
            pages: Math.ceil((res.data.lockets?.length || 0) / 25),
          });
        } else {
          res = await api.get("/admin/qrs", {
            params: { page: currentPage, limit: 25 },
            headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
          });
          setLockets(res.data.lockets || []);
          setFilteredLockets(res.data.lockets || []);
          setPagination(res.data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
        }
      } catch (err) {
        setError("Failed to fetch lockets.");
      } finally {
        setLoading(false);
      }
    };
    fetchLockets();
  }, [isAdmin, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.status, filters.search, filters.dateFrom, filters.dateTo]);

  // Fetch orders
  useEffect(() => {
    if (!isAdmin || activeTab !== "orders") return;
    const fetchOrders = async () => {
      setOrdersLoading(true);
      setOrdersError(null);
      try {
        const res = await api.get("/order", {
          params: { page: ordersCurrentPage, limit: 25 },
          headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
        });
        setOrders(res.data.orders || []);
        setOrdersPagination(res.data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
      } catch (err) {
        setOrdersError("Failed to fetch orders.");
      } finally {
        setOrdersLoading(false);
      }
    };
    fetchOrders();
  }, [isAdmin, activeTab, ordersCurrentPage]);

  // Filter lockets based on filters (only for mock data, as backend handles filtering)
  useEffect(() => {
    if (!shouldUseMockData()) {
      // For real API, filtered lockets are already paginated from backend
      return;
    }

    let filtered = lockets;

    // Status filter
    if (filters.status) {
      filtered = filtered.filter((locket) => locket.status === filters.status);
    }

    // Search filter
    if (filters.search) {
      filtered = filtered.filter(
        (locket) =>
          locket.locketId.toLowerCase().includes(filters.search.toLowerCase()) ||
          (locket.customer?.name && locket.customer.name.toLowerCase().includes(filters.search.toLowerCase())) ||
          (locket.customer?.phone && locket.customer.phone.includes(filters.search))
      );
    }

    // Date filters
    if (filters.dateFrom) {
      filtered = filtered.filter((locket) => new Date(locket.registeredAt) >= new Date(filters.dateFrom));
    }

    if (filters.dateTo) {
      filtered = filtered.filter((locket) => new Date(locket.registeredAt) <= new Date(filters.dateTo));
    }

    setFilteredLockets(filtered);
  }, [lockets, filters]);

  // Register and sell locket in one request
  const onRegisterAndSell = async (data) => {
    setSuccessMsg(null);
    setError(null);
    try {
      let response;
      if (shouldUseMockData()) {
        response = await mockApiService.registerAndSellLocket(data.locketId);
      } else {
        response = await api.post(
          "/admin/qr/register-and-sell",
          { locketId: data.locketId },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
          }
        );
      }
      setSuccessMsg(response.data.message || "Locket registered and marked as sold successfully!");
      reset();
      // Refresh the lockets list
      let res;
      if (shouldUseMockData()) {
        res = await mockApiService.getAdminLockets();
        const startIndex = (currentPage - 1) * 25;
        const endIndex = startIndex + 25;
        const paginatedLockets = (res.data.lockets || []).slice(startIndex, endIndex);
        setLockets(res.data.lockets || []);
        setFilteredLockets(paginatedLockets);
        setPagination({
          page: currentPage,
          limit: 25,
          total: res.data.lockets?.length || 0,
          pages: Math.ceil((res.data.lockets?.length || 0) / 25),
        });
      } else {
        res = await api.get("/admin/qrs", {
          params: { page: currentPage, limit: 25 },
          headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
        });
        setLockets(res.data.lockets || []);
        setFilteredLockets(res.data.lockets || []);
        setPagination(res.data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || "Failed to register and sell locket.";
      setError(errorMessage);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin");
  };

  const handleQRScan = (scannedText) => {

    // Extract locket ID from scanned text
    // Assume the QR code contains either just the locket ID or a URL with the locket ID
    let locketId = scannedText;

    // If it's a URL, extract the locket ID (support /qr/ID or short /ID e.g. emergencyqr.in/DEMO001)
    if (scannedText.includes("/qr/")) {
      const match = scannedText.match(/\/qr\/([^/?]+)/);
      if (match && match[1]) locketId = match[1];
    } else {
      const shortMatch = scannedText.match(/\/([A-Za-z0-9]{6,})(?:\?|$|\/)/);
      if (shortMatch && shortMatch[1]) locketId = shortMatch[1];
    }

    // Pre-populate the form with the scanned locket ID
    setValue("locketId", locketId);
    setShowQRScanner(false);
    setSuccessMsg(`QR Code scanned! Locket ID: ${locketId}`);
  };

  const handleOpenQRScanner = () => {
    setShowQRScanner(true);
    setSuccessMsg(null);
    setError(null);
  };

  const handleBulkGenerate = async (e) => {
    e.preventDefault();
    setBulkGenerateLoading(true);
    setBulkGenerateSuccess(null);
    setBulkGenerateError(null);
    setGeneratedIds([]);

    const count = parseInt(bulkGenerate.count);
    const length = parseInt(bulkGenerate.length);

    // Validation
    if (isNaN(count) || count < 1 || count > 100) {
      setBulkGenerateError("Count must be between 1 and 100");
      setBulkGenerateLoading(false);
      return;
    }

    if (isNaN(length) || length < 6 || length > 20) {
      setBulkGenerateError("Length must be between 6 and 20");
      setBulkGenerateLoading(false);
      return;
    }

    try {
      let res;
      if (shouldUseMockData()) {
        res = await mockApiService.bulkGenerateLockets(count, length);
      } else {
        res = await api.post(
          "/admin/qr/generate-bulk",
          { count, length },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
          }
        );
      }

      setBulkGenerateSuccess(res.data.message);
      setGeneratedIds(res.data.locketIds);
      setBulkGenerate({ count: "", length: "" });

      // Refresh the lockets list
      let locketsRes;
      if (shouldUseMockData()) {
        locketsRes = await mockApiService.getAdminLockets();
        const startIndex = (currentPage - 1) * 25;
        const endIndex = startIndex + 25;
        const paginatedLockets = (locketsRes.data.lockets || []).slice(startIndex, endIndex);
        setLockets(locketsRes.data.lockets || []);
        setFilteredLockets(paginatedLockets);
        setPagination({
          page: currentPage,
          limit: 25,
          total: locketsRes.data.lockets?.length || 0,
          pages: Math.ceil((locketsRes.data.lockets?.length || 0) / 25),
        });
      } else {
        locketsRes = await api.get("/admin/qrs", {
          params: { page: currentPage, limit: 25 },
          headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
        });
        setLockets(locketsRes.data.lockets || []);
        setFilteredLockets(locketsRes.data.lockets || []);
        setPagination(locketsRes.data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
      }
    } catch (err) {
      setBulkGenerateError(err.response?.data?.error || "Failed to generate locket IDs");
    } finally {
      setBulkGenerateLoading(false);
    }
  };

  const copyGeneratedIds = () => {
    const idsText = generatedIds.join("\n");
    navigator.clipboard.writeText(idsText).then(() => {
      alert("Locket IDs copied to clipboard!");
    });
  };

  const handleEditLocket = (locket) => {
    setEditingLocket(locket);
    setEditFormData({
      status: locket.status || "",
      customerName: locket.customer?.name || "",
      customerPhone: locket.customer?.phone || "",
      customerEmail: locket.customer?.email || "",
    });
    setEditSuccess(null);
    setEditError(null);
  };

  const handleDeleteLocket = async (locketId) => {
    if (!window.confirm(`Are you sure you want to delete locket ${locketId}? This action cannot be undone.`)) {
      return;
    }

    try {
      if (shouldUseMockData()) {
        await mockApiService.deleteLocket(locketId);
        setSuccessMsg(`Locket ${locketId} deleted successfully`);
        // Refresh lockets list
        const locketsRes = await mockApiService.getAdminLockets();
        const startIndex = (currentPage - 1) * 25;
        const endIndex = startIndex + 25;
        const paginatedLockets = (locketsRes.data.lockets || []).slice(startIndex, endIndex);
        setLockets(locketsRes.data.lockets || []);
        setFilteredLockets(paginatedLockets);
        setPagination({
          page: currentPage,
          limit: 25,
          total: locketsRes.data.lockets?.length || 0,
          pages: Math.ceil((locketsRes.data.lockets?.length || 0) / 25),
        });
      } else {
        const response = await api.delete(`/admin/qr/${locketId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
        });
        setSuccessMsg(response.data.message || `Locket ${locketId} deleted successfully`);

        // Refresh lockets list
        const locketsRes = await api.get("/admin/qrs", {
          params: { page: currentPage, limit: 25 },
          headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
        });
        setLockets(locketsRes.data.lockets || []);
        setFilteredLockets(locketsRes.data.lockets || []);
        setPagination(locketsRes.data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
      }
      setError(null);
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Failed to delete locket";
      setError(errorMessage);
      setSuccessMsg(null);
    }
  };

  const handleCloseEditModal = () => {
    setEditingLocket(null);
    setEditFormData({
      status: "",
      customerName: "",
      customerPhone: "",
      customerEmail: "",
    });
    setEditSuccess(null);
    setEditError(null);
  };

  const handleUpdateLocket = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditSuccess(null);
    setEditError(null);

    try {
      if (shouldUseMockData()) {
        await mockApiService.updateLocket(editingLocket.locketId, {
          status: editFormData.status,
          customerName: editFormData.customerName,
          customerPhone: editFormData.customerPhone,
          customerEmail: editFormData.customerEmail,
        });
      } else {
        await api.put(
          `/admin/qr/${editingLocket.locketId}/update`,
          {
            status: editFormData.status,
            customerName: editFormData.customerName,
            customerPhone: editFormData.customerPhone,
            customerEmail: editFormData.customerEmail,
          },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
          }
        );
      }

      setEditSuccess("Locket updated successfully!");

      // Refresh the lockets list
      let res;
      if (shouldUseMockData()) {
        res = await mockApiService.getAdminLockets();
        const startIndex = (currentPage - 1) * 25;
        const endIndex = startIndex + 25;
        const paginatedLockets = (res.data.lockets || []).slice(startIndex, endIndex);
        setLockets(res.data.lockets || []);
        setFilteredLockets(paginatedLockets);
        setPagination({
          page: currentPage,
          limit: 25,
          total: res.data.lockets?.length || 0,
          pages: Math.ceil((res.data.lockets?.length || 0) / 25),
        });
      } else {
        res = await api.get("/admin/qrs", {
          params: { page: currentPage, limit: 25 },
          headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
        });
        setLockets(res.data.lockets || []);
        setFilteredLockets(res.data.lockets || []);
        setPagination(res.data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
      }

      // Close modal after a short delay
      setTimeout(() => {
        handleCloseEditModal();
      }, 1500);
    } catch (err) {
      setEditError(err.response?.data?.error || "Failed to update locket");
    } finally {
      setEditLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = [
      "Locket ID",
      "Status",
      "Customer Name",
      "Customer Phone",
      "Customer Email",
      "Registered At",
      "Sold At",
      "Activated At",
      "Scan Count",
    ];

    const csvData = filteredLockets.map((locket) => [
      locket.locketId,
      locket.status,
      locket.customer?.name || "",
      locket.customer?.phone || "",
      locket.customer?.email || "",
      locket.registeredAt ? new Date(locket.registeredAt).toLocaleDateString() : "",
      locket.soldAt ? new Date(locket.soldAt).toLocaleDateString() : "",
      locket.activatedAt ? new Date(locket.activatedAt).toLocaleDateString() : "",
      locket.scanCount || 0,
    ]);

    const csvContent = [headers, ...csvData].map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `lockets_${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isAdmin) return null;
  return (
    <div style={commonStyles.mobileContainer}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: spacing.lg,
          flexWrap: "wrap",
          gap: spacing.md,
        }}
      >
        <div>
          <h1
            style={{
              ...commonStyles.title,
              fontSize: "28px",
              marginBottom: spacing.xs,
              textAlign: "left",
            }}
          >
            🔧 Admin Dashboard
          </h1>
          <p
            style={{
              ...commonStyles.text,
              margin: 0,
              fontSize: "14px",
            }}
          >
            Manage lockets, track sales, and monitor system usage
          </p>
        </div>
        <button
          onClick={handleLogout}
          style={{
            ...createButtonStyle("outline"),
            fontSize: "14px",
          }}
        >
          🚪 Logout
        </button>
      </div>

      {/* Bulk Generate Locket IDs Section */}
      <div
        style={{
          ...commonStyles.card,
          marginBottom: spacing.lg,
          background: `linear-gradient(135deg, ${colors.primary}10 0%, ${colors.secondary}10 100%)`,
          border: `2px solid ${colors.primary}40`,
        }}
      >
        <h2
          style={{
            ...commonStyles.subtitle,
            marginBottom: spacing.sm,
            display: "flex",
            alignItems: "center",
            gap: spacing.sm,
          }}
        >
          <span style={{ fontSize: "24px" }}>🏭</span>
          Bulk Generate Locket IDs
        </h2>
        <p style={{ ...commonStyles.text, marginBottom: spacing.lg, color: colors.gray[600] }}>
          Generate multiple unique locket IDs in bulk. All generated IDs will be created with 'registered' status.
        </p>

        <form onSubmit={handleBulkGenerate}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: window.innerWidth >= 768 ? "1fr 1fr" : "1fr",
              gap: spacing.md,
              marginBottom: spacing.lg,
            }}
          >
            <div>
              <label style={commonStyles.label}>
                Number of Locket IDs <span style={{ color: colors.danger }}>*</span>
              </label>
              <input
                type="number"
                value={bulkGenerate.count}
                onChange={(e) => setBulkGenerate({ ...bulkGenerate, count: e.target.value })}
                placeholder="Enter count (1-100)"
                min="1"
                max="100"
                style={{
                  ...createInputStyle(),
                  fontSize: "16px",
                  fontWeight: "600",
                }}
                required
              />
              <div style={{ fontSize: "12px", color: colors.gray[500], marginTop: spacing.xs }}>Min: 1, Max: 100</div>
            </div>

            <div>
              <label style={commonStyles.label}>
                Locket ID Length <span style={{ color: colors.danger }}>*</span>
              </label>
              <input
                type="number"
                value={bulkGenerate.length}
                onChange={(e) => setBulkGenerate({ ...bulkGenerate, length: e.target.value })}
                placeholder="Enter length (6-20)"
                min="6"
                max="20"
                style={{
                  ...createInputStyle(),
                  fontSize: "16px",
                  fontWeight: "600",
                }}
                required
              />
              <div style={{ fontSize: "12px", color: colors.gray[500], marginTop: spacing.xs }}>
                Min: 6, Max: 20 characters
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={bulkGenerateLoading}
            style={{
              ...createButtonStyle("primary", "large"),
              width: "100%",
              fontSize: "16px",
              fontWeight: "700",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: spacing.sm,
            }}
          >
            {bulkGenerateLoading ? (
              <>🔄 Generating...</>
            ) : (
              <>
                <span style={{ fontSize: "20px" }}>⚡</span>
                Generate Locket IDs
              </>
            )}
          </button>

          {bulkGenerateSuccess && (
            <div
              style={{
                backgroundColor: colors.success + "15",
                border: `2px solid ${colors.success}`,
                borderRadius: borderRadius.lg,
                padding: spacing.lg,
                marginTop: spacing.lg,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: spacing.sm, marginBottom: spacing.md }}>
                <span style={{ fontSize: "24px" }}>✅</span>
                <p style={{ color: colors.success, margin: 0, fontSize: "16px", fontWeight: "700" }}>{bulkGenerateSuccess}</p>
              </div>

              {generatedIds.length > 0 && (
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: spacing.sm,
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        color: colors.gray[900],
                        margin: 0,
                      }}
                    >
                      Generated Locket IDs:
                    </h4>
                    <button
                      type="button"
                      onClick={copyGeneratedIds}
                      style={{
                        ...createButtonStyle("outline", "small"),
                        fontSize: "12px",
                      }}
                    >
                      📋 Copy All
                    </button>
                  </div>
                  <div
                    style={{
                      backgroundColor: colors.white,
                      borderRadius: borderRadius.md,
                      padding: spacing.md,
                      maxHeight: "200px",
                      overflowY: "auto",
                      border: `1px solid ${colors.gray[200]}`,
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                        gap: spacing.sm,
                      }}
                    >
                      {generatedIds.map((id, idx) => (
                        <div
                          key={idx}
                          style={{
                            fontFamily: "monospace",
                            fontSize: "12px",
                            fontWeight: "700",
                            padding: `${spacing.xs} ${spacing.sm}`,
                            backgroundColor: colors.gray[50],
                            borderRadius: borderRadius.sm,
                            textAlign: "center",
                            border: `1px solid ${colors.gray[200]}`,
                          }}
                        >
                          {id}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {bulkGenerateError && (
            <div
              style={{
                backgroundColor: colors.danger + "15",
                border: `2px solid ${colors.danger}`,
                borderRadius: borderRadius.lg,
                padding: spacing.lg,
                marginTop: spacing.lg,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: spacing.sm }}>
                <span style={{ fontSize: "20px" }}>⚠️</span>
                <p style={{ color: colors.danger, margin: 0, fontSize: "14px", fontWeight: "600" }}>{bulkGenerateError}</p>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Register and Sell Locket Section */}
      <div
        style={{
          ...commonStyles.card,
          marginBottom: spacing.lg,
        }}
      >
        <h2
          style={{
            ...commonStyles.subtitle,
            marginBottom: spacing.md,
          }}
        >
          📦 Register & Sell Locket
        </h2>
        <form onSubmit={handleSubmit(onRegisterAndSell)}>
          <div
            style={{
              display: "flex",
              gap: spacing.sm,
              alignItems: "flex-start",
              marginBottom: spacing.sm,
            }}
          >
            <div style={{ flex: 1 }}>
              <input
                type="text"
                placeholder="Enter Locket ID"
                {...register("locketId", { required: true })}
                style={createInputStyle(errors.locketId)}
              />
              {errors.locketId && <span style={commonStyles.errorText}>Locket ID required</span>}
            </div>
            <button
              type="button"
              onClick={handleOpenQRScanner}
              style={{
                ...createButtonStyle("secondary"),
                minWidth: "120px",
                height: "40px",
              }}
            >
              📷 Scan QR
            </button>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...createButtonStyle("primary", "large"),
              width: "100%",
            }}
          >
            {isSubmitting ? "🔄 Processing..." : "✅ Register & Sell"}
          </button>
          {successMsg && (
            <div
              style={{
                ...commonStyles.card,
                backgroundColor: colors.success + "10",
                border: `1px solid ${colors.success}`,
                marginTop: spacing.md,
              }}
            >
              <p style={{ color: colors.success, margin: 0, fontSize: "14px" }}>✅ {successMsg}</p>
            </div>
          )}
          {error && (
            <div
              style={{
                ...commonStyles.card,
                backgroundColor: colors.danger + "10",
                border: `1px solid ${colors.danger}`,
                marginTop: spacing.md,
              }}
            >
              <p style={{ color: colors.danger, margin: 0, fontSize: "14px" }}>❌ {error}</p>
            </div>
          )}
        </form>
      </div>

      {/* Tabs for Lockets and Orders */}
      <div
        style={{
          ...commonStyles.card,
          marginBottom: spacing.lg,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: spacing.sm,
            borderBottom: `2px solid ${colors.gray[200]}`,
            marginBottom: spacing.lg,
          }}
        >
          <button
            onClick={() => setActiveTab("lockets")}
            style={{
              padding: `${spacing.sm} ${spacing.lg}`,
              border: "none",
              background: "none",
              fontSize: "16px",
              fontWeight: "600",
              color: activeTab === "lockets" ? colors.primary : colors.gray[600],
              borderBottom: activeTab === "lockets" ? `3px solid ${colors.primary}` : "3px solid transparent",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            📊 All Lockets
          </button>
          <button
            onClick={() => setActiveTab("orders")}
            style={{
              padding: `${spacing.sm} ${spacing.lg}`,
              border: "none",
              background: "none",
              fontSize: "16px",
              fontWeight: "600",
              color: activeTab === "orders" ? colors.primary : colors.gray[600],
              borderBottom: activeTab === "orders" ? `3px solid ${colors.primary}` : "3px solid transparent",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            📦 Orders
          </button>
        </div>

        {activeTab === "lockets" && (
          <>
            <h2
              style={{
                ...commonStyles.subtitle,
                marginBottom: spacing.md,
              }}
            >
              📊 All Lockets
            </h2>

            {/* Filters */}
            <div
              style={{
                backgroundColor: colors.gray[50],
                padding: spacing.md,
                borderRadius: borderRadius.md,
                marginBottom: spacing.md,
              }}
            >
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: colors.gray[800],
                  marginBottom: spacing.md,
                }}
              >
                🔍 Filters
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: spacing.sm,
                  marginBottom: spacing.md,
                }}
              >
                <div>
                  <label style={commonStyles.label}>Status:</label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    style={createInputStyle()}
                  >
                    <option value="">All Statuses</option>
                    <option value="registered">Registered</option>
                    <option value="sold">Sold</option>
                    <option value="activated">Activated</option>
                  </select>
                </div>
                <div>
                  <label style={commonStyles.label}>Search:</label>
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    placeholder="Locket ID, Name, or Phone"
                    style={createInputStyle()}
                  />
                </div>
                <div>
                  <label style={commonStyles.label}>Date From:</label>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    style={createInputStyle()}
                  />
                </div>
                <div>
                  <label style={commonStyles.label}>Date To:</label>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    style={createInputStyle()}
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: spacing.sm,
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={() => setFilters({ status: "", search: "", dateFrom: "", dateTo: "" })}
                  style={{
                    ...createButtonStyle("outline"),
                    fontSize: "14px",
                  }}
                >
                  🔄 Clear Filters
                </button>
                <button
                  onClick={exportToCSV}
                  style={{
                    ...createButtonStyle("success"),
                    fontSize: "14px",
                  }}
                >
                  📊 Export CSV ({filteredLockets.length})
                </button>
              </div>
            </div>

            {loading ? (
              <div style={commonStyles.loading}>
                <div style={{ fontSize: "24px", marginBottom: spacing.md }}>🔄</div>
                <div>Loading lockets...</div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    ...commonStyles.table,
                    fontSize: "13px",
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ minWidth: "120px" }}>Locket ID</th>
                      <th style={{ minWidth: "100px" }}>Status</th>
                      <th style={{ minWidth: "120px" }}>Customer</th>
                      <th style={{ minWidth: "120px" }}>Phone</th>
                      <th style={{ minWidth: "150px" }}>Email</th>
                      <th style={{ minWidth: "100px" }}>Registered</th>
                      <th style={{ minWidth: "100px" }}>Sold</th>
                      <th style={{ minWidth: "100px" }}>Activated</th>
                      <th style={{ minWidth: "80px" }}>Scans</th>
                      <th style={{ minWidth: "150px" }}>Short URL</th>
                      <th style={{ minWidth: "100px" }}>QR Code</th>
                      <th style={{ minWidth: "100px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLockets.map((l) => (
                      <tr key={l.locketId}>
                        <td style={{ fontFamily: "monospace", fontWeight: "500" }}>{l.locketId}</td>
                        <td>
                          <span
                            style={{
                              ...createBadgeStyle(
                                l.status === "activated" ? "success" : l.status === "sold" ? "warning" : "danger"
                              ),
                              fontSize: "11px",
                            }}
                          >
                            {l.status}
                          </span>
                        </td>
                        <td style={{ color: colors.gray[700] }}>{l.customer?.name || "-"}</td>
                        <td style={{ color: colors.gray[700] }}>{l.customer?.phone || "-"}</td>
                        <td style={{ color: colors.gray[700] }}>{l.customer?.email || "-"}</td>
                        <td style={{ color: colors.gray[600], fontSize: "12px" }}>
                          {l.registeredAt ? new Date(l.registeredAt).toLocaleDateString() : "-"}
                        </td>
                        <td style={{ color: colors.gray[600], fontSize: "12px" }}>
                          {l.soldAt ? new Date(l.soldAt).toLocaleDateString() : "-"}
                        </td>
                        <td style={{ color: colors.gray[600], fontSize: "12px" }}>
                          {l.activatedAt ? new Date(l.activatedAt).toLocaleDateString() : "-"}
                        </td>
                        <td style={{ textAlign: "center", fontWeight: "500" }}>{l.scanCount || 0}</td>
                        <td style={{ fontSize: "12px" }}>
                          {l.shortUrl ? (
                            <a
                              href={l.shortUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: colors.primary, textDecoration: "underline", wordBreak: "break-all" }}
                            >
                              {l.shortUrl}
                            </a>
                          ) : (
                            <span style={{ color: colors.gray[400] }}>-</span>
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {l.qrCodeImage || l.qrCodeImageLight ? (
                            <div style={{ display: "flex", gap: "8px", justifyContent: "center", alignItems: "center" }}>
                              {l.qrCodeImage && (
                                <div style={{ position: "relative", display: "inline-block" }}>
                                  <img
                                    src={l.qrCodeImage}
                                    alt="QR Code (Black)"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      cursor: "pointer",
                                      border: `1px solid ${colors.gray[300]}`,
                                      borderRadius: borderRadius.sm,
                                      backgroundColor: "#ffffff", // White background for preview (QR has transparent bg)
                                    }}
                                    onClick={() => {
                                      const newWindow = window.open("", "_blank");
                                      newWindow.document.write(`
                                    <html>
                                      <head><title>QR Code (Black) - ${l.locketId}</title></head>
                                      <body style="margin:0;padding:20px;text-align:center;font-family:Arial;background:linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%);background-size:20px 20px;background-position:0 0, 0 10px, 10px -10px, -10px 0px;">
                                        <h2>QR Code (Black) - ${l.locketId}</h2>
                                        <p style="color:#666;">Transparent background - suitable for light surfaces</p>
                                        <img src="${
                                          l.qrCodeImage
                                        }" style="max-width:400px;background:white;padding:20px;border:1px solid #ccc;border-radius:8px;" />
                                        <p>Short URL: <a href="${l.shortUrl || "#"}" target="_blank">${
                                        l.shortUrl || "N/A"
                                      }</a></p>
                                      </body>
                                    </html>
                                  `);
                                    }}
                                    title="Click to view full size (Black QR - transparent background)"
                                  />
                                </div>
                              )}
                              {l.qrCodeImageLight && (
                                <div style={{ position: "relative", display: "inline-block" }}>
                                  <img
                                    src={l.qrCodeImageLight}
                                    alt="QR Code (White)"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      cursor: "pointer",
                                      border: `1px solid ${colors.gray[300]}`,
                                      borderRadius: borderRadius.sm,
                                      backgroundColor: "#000000", // Black background for preview (QR has transparent bg)
                                    }}
                                    onClick={() => {
                                      const newWindow = window.open("", "_blank");
                                      newWindow.document.write(`
                                    <html>
                                      <head><title>QR Code (White) - ${l.locketId}</title></head>
                                      <body style="margin:0;padding:20px;text-align:center;font-family:Arial;background:linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%);background-size:20px 20px;background-position:0 0, 0 10px, 10px -10px, -10px 0px;">
                                        <h2>QR Code (White) - ${l.locketId}</h2>
                                        <p style="color:#666;">Transparent background - suitable for dark surfaces</p>
                                        <img src="${
                                          l.qrCodeImageLight
                                        }" style="max-width:400px;background:black;padding:20px;border:1px solid #ccc;border-radius:8px;" />
                                        <p>Short URL: <a href="${l.shortUrl || "#"}" target="_blank">${
                                        l.shortUrl || "N/A"
                                      }</a></p>
                                      </body>
                                    </html>
                                  `);
                                    }}
                                    title="Click to view full size (White QR - transparent background)"
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: colors.gray[400] }}>-</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                            <button
                              onClick={() => handleEditLocket(l)}
                              style={{
                                ...createButtonStyle("primary"),
                                fontSize: "12px",
                                padding: "6px 12px",
                              }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDeleteLocket(l.locketId)}
                              style={{
                                ...createButtonStyle("danger"),
                                fontSize: "12px",
                                padding: "6px 12px",
                              }}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredLockets.length === 0 && (
                  <div
                    style={{
                      ...commonStyles.emptyState,
                      padding: spacing.xxl,
                    }}
                  >
                    <div style={{ fontSize: "48px", marginBottom: spacing.md }}>📭</div>
                    <div>No lockets found matching the current filters.</div>
                  </div>
                )}

                {/* Pagination Controls */}
                {pagination.pages > 1 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: spacing.sm,
                      marginTop: spacing.xl,
                      padding: spacing.lg,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      style={{
                        ...createButtonStyle("outline"),
                        padding: `${spacing.sm} ${spacing.md}`,
                        fontSize: "14px",
                        opacity: currentPage === 1 ? 0.5 : 1,
                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      }}
                    >
                      First
                    </button>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      style={{
                        ...createButtonStyle("outline"),
                        padding: `${spacing.sm} ${spacing.md}`,
                        fontSize: "14px",
                        opacity: currentPage === 1 ? 0.5 : 1,
                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      }}
                    >
                      Previous
                    </button>
                    <span
                      style={{
                        padding: `${spacing.sm} ${spacing.md}`,
                        fontSize: "14px",
                        color: colors.gray[700],
                        fontWeight: "600",
                      }}
                    >
                      Page {currentPage} of {pagination.pages} ({pagination.total} total)
                    </span>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(pagination.pages, prev + 1))}
                      disabled={currentPage === pagination.pages}
                      style={{
                        ...createButtonStyle("outline"),
                        padding: `${spacing.sm} ${spacing.md}`,
                        fontSize: "14px",
                        opacity: currentPage === pagination.pages ? 0.5 : 1,
                        cursor: currentPage === pagination.pages ? "not-allowed" : "pointer",
                      }}
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setCurrentPage(pagination.pages)}
                      disabled={currentPage === pagination.pages}
                      style={{
                        ...createButtonStyle("outline"),
                        padding: `${spacing.sm} ${spacing.md}`,
                        fontSize: "14px",
                        opacity: currentPage === pagination.pages ? 0.5 : 1,
                        cursor: currentPage === pagination.pages ? "not-allowed" : "pointer",
                      }}
                    >
                      Last
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === "orders" && (
          <>
            <h2
              style={{
                ...commonStyles.subtitle,
                marginBottom: spacing.md,
              }}
            >
              📦 Orders
            </h2>

            {ordersLoading ? (
              <div style={{ ...commonStyles.loading, padding: spacing.xxl }}>
                <div>Loading orders...</div>
              </div>
            ) : ordersError ? (
              <div
                style={{
                  backgroundColor: colors.danger + "15",
                  border: `2px solid ${colors.danger}`,
                  borderRadius: borderRadius.lg,
                  padding: spacing.lg,
                  marginBottom: spacing.lg,
                }}
              >
                <p style={{ color: colors.danger, margin: 0, fontSize: "14px", fontWeight: "600" }}>❌ {ordersError}</p>
              </div>
            ) : (
              <>
                <table style={{ ...commonStyles.table, width: "100%", marginBottom: spacing.lg }}>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Address</th>
                      <th>Status</th>
                      <th>QR Code ID</th>
                      <th>Order Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id}>
                        <td style={{ fontFamily: "monospace", fontSize: "12px" }}>{order.id.slice(-8)}</td>
                        <td>{order.name}</td>
                        <td>{order.phone}</td>
                        <td>{order.email}</td>
                        <td style={{ fontSize: "12px", maxWidth: "200px" }}>
                          {order.addressLine1}
                          {order.addressLine2 && `, ${order.addressLine2}`}
                          {order.addressLine3 && `, ${order.addressLine3}`}
                          <br />
                          {order.areaColony}, {order.city}, {order.state} - {order.pinCode}
                          {order.landmark && <br />}
                          {order.landmark && `Near: ${order.landmark}`}
                        </td>
                        <td>
                          <span
                            style={{
                              ...createBadgeStyle(
                                order.status === "Delivered"
                                  ? "success"
                                  : order.status === "Shipped"
                                  ? "warning"
                                  : order.status === "In Progress"
                                  ? "gray"
                                  : "danger"
                              ),
                              fontSize: "11px",
                            }}
                          >
                            {order.status}
                          </span>
                        </td>
                        <td style={{ fontFamily: "monospace", fontWeight: "600" }}>{order.qrCodeId || "-"}</td>
                        <td style={{ fontSize: "12px" }}>{new Date(order.createdAt).toLocaleDateString()}</td>
                        <td>
                          <button
                            onClick={() => {
                              setEditingOrder(order);
                              setOrderEditFormData({
                                status: order.status,
                                qrCodeId: order.qrCodeId || "",
                              });
                            }}
                            style={{
                              ...createButtonStyle("primary"),
                              fontSize: "12px",
                              padding: "6px 12px",
                            }}
                          >
                            ✏️ Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {orders.length === 0 && (
                  <div
                    style={{
                      ...commonStyles.emptyState,
                      padding: spacing.xxl,
                    }}
                  >
                    <div style={{ fontSize: "48px", marginBottom: spacing.md }}>📭</div>
                    <div>No orders found.</div>
                  </div>
                )}

                {/* Orders Pagination Controls */}
                {ordersPagination.pages > 1 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: spacing.sm,
                      marginTop: spacing.xl,
                      padding: spacing.lg,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      onClick={() => setOrdersCurrentPage(1)}
                      disabled={ordersCurrentPage === 1}
                      style={{
                        ...createButtonStyle("outline"),
                        padding: `${spacing.sm} ${spacing.md}`,
                        fontSize: "14px",
                        opacity: ordersCurrentPage === 1 ? 0.5 : 1,
                        cursor: ordersCurrentPage === 1 ? "not-allowed" : "pointer",
                      }}
                    >
                      First
                    </button>
                    <button
                      onClick={() => setOrdersCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={ordersCurrentPage === 1}
                      style={{
                        ...createButtonStyle("outline"),
                        padding: `${spacing.sm} ${spacing.md}`,
                        fontSize: "14px",
                        opacity: ordersCurrentPage === 1 ? 0.5 : 1,
                        cursor: ordersCurrentPage === 1 ? "not-allowed" : "pointer",
                      }}
                    >
                      Previous
                    </button>
                    <span
                      style={{
                        padding: `${spacing.sm} ${spacing.md}`,
                        fontSize: "14px",
                        color: colors.gray[700],
                        fontWeight: "600",
                      }}
                    >
                      Page {ordersCurrentPage} of {ordersPagination.pages} ({ordersPagination.total} total)
                    </span>
                    <button
                      onClick={() => setOrdersCurrentPage((prev) => Math.min(ordersPagination.pages, prev + 1))}
                      disabled={ordersCurrentPage === ordersPagination.pages}
                      style={{
                        ...createButtonStyle("outline"),
                        padding: `${spacing.sm} ${spacing.md}`,
                        fontSize: "14px",
                        opacity: ordersCurrentPage === ordersPagination.pages ? 0.5 : 1,
                        cursor: ordersCurrentPage === ordersPagination.pages ? "not-allowed" : "pointer",
                      }}
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setOrdersCurrentPage(ordersPagination.pages)}
                      disabled={ordersCurrentPage === ordersPagination.pages}
                      style={{
                        ...createButtonStyle("outline"),
                        padding: `${spacing.sm} ${spacing.md}`,
                        fontSize: "14px",
                        opacity: ordersCurrentPage === ordersPagination.pages ? 0.5 : 1,
                        cursor: ordersCurrentPage === ordersPagination.pages ? "not-allowed" : "pointer",
                      }}
                    >
                      Last
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <QRScanner isOpen={showQRScanner} onScan={handleQRScan} onClose={() => setShowQRScanner(false)} />

      {/* Edit Order Modal */}
      {editingOrder && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            padding: spacing.md,
          }}
          onClick={() => {
            setEditingOrder(null);
            setOrderEditFormData({ status: "", qrCodeId: "" });
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: borderRadius.lg,
              padding: spacing.xl,
              maxWidth: "500px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: spacing.lg,
              }}
            >
              <h2 style={{ fontSize: "20px", fontWeight: "700", color: colors.gray[900] }}>Edit Order</h2>
              <button
                onClick={() => {
                  setEditingOrder(null);
                  setOrderEditFormData({ status: "", qrCodeId: "" });
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: colors.gray[500],
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ marginBottom: spacing.md }}>
              <p style={{ fontSize: "14px", color: colors.gray[600], marginBottom: spacing.sm }}>
                <strong>Order ID:</strong> {editingOrder.id.slice(-8)}
              </p>
              <p style={{ fontSize: "14px", color: colors.gray[600], marginBottom: spacing.sm }}>
                <strong>Name:</strong> {editingOrder.name}
              </p>
              <p style={{ fontSize: "14px", color: colors.gray[600], marginBottom: spacing.lg }}>
                <strong>Phone:</strong> {editingOrder.phone} | <strong>Email:</strong> {editingOrder.email}
              </p>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await api.put(
                    `/order/${editingOrder.id}`,
                    {
                      status: orderEditFormData.status,
                      qrCodeId: orderEditFormData.qrCodeId || null,
                    },
                    {
                      headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
                    }
                  );
                  // Refresh orders
                  const res = await api.get("/order", {
                    params: { page: ordersCurrentPage, limit: 25 },
                    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
                  });
                  setOrders(res.data.orders || []);
                  setOrdersPagination(res.data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
                  setEditingOrder(null);
                  setOrderEditFormData({ status: "", qrCodeId: "" });
                } catch (err) {
                  alert(err.response?.data?.error || "Failed to update order");
                }
              }}
            >
              <div style={{ marginBottom: spacing.md }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colors.gray[700],
                    marginBottom: spacing.xs,
                  }}
                >
                  Status
                </label>
                <select
                  value={orderEditFormData.status}
                  onChange={(e) => setOrderEditFormData({ ...orderEditFormData, status: e.target.value })}
                  style={{
                    ...createInputStyle(),
                    width: "100%",
                    fontSize: "16px",
                    padding: spacing.sm,
                  }}
                  required
                >
                  <option value="Received">Received</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Shipped">Shipped</option>
                  <option value="Delivered">Delivered</option>
                </select>
              </div>

              <div style={{ marginBottom: spacing.lg }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colors.gray[700],
                    marginBottom: spacing.xs,
                  }}
                >
                  QR Code ID
                </label>
                <input
                  type="text"
                  value={orderEditFormData.qrCodeId}
                  onChange={(e) => setOrderEditFormData({ ...orderEditFormData, qrCodeId: e.target.value.toUpperCase() })}
                  style={{
                    ...createInputStyle(),
                    width: "100%",
                    fontSize: "16px",
                    padding: spacing.sm,
                    fontFamily: "monospace",
                  }}
                  placeholder="Enter QR Code ID (optional)"
                />
              </div>

              <div style={{ display: "flex", gap: spacing.md }}>
                <button
                  type="button"
                  onClick={() => {
                    setEditingOrder(null);
                    setOrderEditFormData({ status: "", qrCodeId: "" });
                  }}
                  style={{
                    ...createButtonStyle("outline"),
                    flex: 1,
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    ...createButtonStyle("primary", "large"),
                    flex: 1,
                    fontSize: "16px",
                    fontWeight: "700",
                  }}
                >
                  ✅ Update Order
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Locket Modal */}
      {editingLocket && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            padding: spacing.md,
          }}
          onClick={handleCloseEditModal}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: borderRadius.lg,
              padding: spacing.xl,
              maxWidth: "500px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: spacing.lg,
              }}
            >
              <h2 style={{ ...commonStyles.subtitle, margin: 0 }}>✏️ Edit Locket</h2>
              <button
                onClick={handleCloseEditModal}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: colors.gray[500],
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                backgroundColor: colors.gray[100],
                padding: spacing.md,
                borderRadius: borderRadius.md,
                marginBottom: spacing.lg,
              }}
            >
              <div style={{ fontSize: "12px", color: colors.gray[600], marginBottom: spacing.xs }}>Locket ID</div>
              <div style={{ fontFamily: "monospace", fontWeight: "600", fontSize: "16px" }}>{editingLocket.locketId}</div>
            </div>

            {editSuccess && (
              <div
                style={{
                  backgroundColor: colors.success + "20",
                  color: colors.success,
                  padding: spacing.md,
                  borderRadius: borderRadius.md,
                  marginBottom: spacing.md,
                  border: `1px solid ${colors.success}`,
                }}
              >
                ✅ {editSuccess}
              </div>
            )}

            {editError && (
              <div
                style={{
                  backgroundColor: colors.danger + "20",
                  color: colors.danger,
                  padding: spacing.md,
                  borderRadius: borderRadius.md,
                  marginBottom: spacing.md,
                  border: `1px solid ${colors.danger}`,
                }}
              >
                ❌ {editError}
              </div>
            )}

            <form onSubmit={handleUpdateLocket}>
              <div style={{ marginBottom: spacing.md }}>
                <label style={commonStyles.label}>
                  Status <span style={{ color: colors.danger }}>*</span>
                </label>
                <select
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                  style={createInputStyle()}
                  required
                >
                  <option value="">Select Status</option>
                  <option value="manufactured">Manufactured</option>
                  <option value="registered">Registered</option>
                  <option value="sold">Sold</option>
                  <option value="activated">Activated</option>
                  <option value="inactive">Inactive</option>
                  <option value="lost">Lost</option>
                  <option value="replaced">Replaced</option>
                </select>
              </div>

              <div style={{ marginBottom: spacing.md }}>
                <label style={commonStyles.label}>Customer Name</label>
                <input
                  type="text"
                  value={editFormData.customerName}
                  onChange={(e) => setEditFormData({ ...editFormData, customerName: e.target.value })}
                  placeholder="Enter customer name"
                  style={createInputStyle()}
                  disabled={!editingLocket.customer}
                />
                {!editingLocket.customer && (
                  <div style={{ fontSize: "12px", color: colors.gray[500], marginTop: spacing.xs }}>
                    Customer info not available (locket not activated)
                  </div>
                )}
              </div>

              <div style={{ marginBottom: spacing.md }}>
                <label style={commonStyles.label}>Customer Phone</label>
                <input
                  type="tel"
                  value={editFormData.customerPhone}
                  onChange={(e) => setEditFormData({ ...editFormData, customerPhone: e.target.value })}
                  placeholder="Enter customer phone"
                  style={createInputStyle()}
                  disabled={!editingLocket.customer}
                />
              </div>

              <div style={{ marginBottom: spacing.lg }}>
                <label style={commonStyles.label}>Customer Email</label>
                <input
                  type="email"
                  value={editFormData.customerEmail}
                  onChange={(e) => setEditFormData({ ...editFormData, customerEmail: e.target.value })}
                  placeholder="Enter customer email"
                  style={createInputStyle()}
                  disabled={!editingLocket.customer}
                />
              </div>

              <div style={{ display: "flex", gap: spacing.md }}>
                <button
                  type="submit"
                  disabled={editLoading}
                  style={{
                    ...createButtonStyle("primary"),
                    flex: 1,
                  }}
                >
                  {editLoading ? "🔄 Updating..." : "💾 Update Locket"}
                </button>
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  style={{
                    ...createButtonStyle("outline"),
                    flex: 1,
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
